import { useState, useEffect, useCallback } from 'react';
import {
  Wallet, Plus, Search, X, Check, AlertCircle, Loader2,
  RefreshCw, Trash2, CheckCircle, XCircle, Clock, RotateCcw
} from 'lucide-react';
import salesPaymentController from '../../controllers/salesPaymentController';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  Completed: { label: 'Completed', color: '#15803d', bg: '#dcfce7', icon: CheckCircle },
  Pending:   { label: 'Pending',   color: '#92400e', bg: '#fef3c7', icon: Clock },
  Failed:    { label: 'Failed',    color: '#b91c1c', bg: '#fef2f2', icon: XCircle },
  Refunded:  { label: 'Refunded',  color: '#6d28d9', bg: '#ede9fe', icon: RotateCcw },
};

const METHOD_CONFIG = {
  Cash:           { label: 'Cash',           color: '#15803d', bg: '#dcfce7' },
  'Bank Transfer':{ label: 'Bank Transfer',  color: '#1d4ed8', bg: '#dbeafe' },
  UPI:            { label: 'UPI',            color: '#6d28d9', bg: '#ede9fe' },
  Cheque:         { label: 'Cheque',         color: '#92400e', bg: '#fef3c7' },
  Card:           { label: 'Card',           color: '#0e7490', bg: '#cffafe' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Spinner = ({ size = 16 }) => <Loader2 size={size} style={{ display: 'inline', animation: 'spin 1s linear infinite' }} />;

const Badge = ({ status, config = STATUS_CONFIG }) => {
  const cfg = config[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color }}>
      {Icon && <Icon size={10} />}{cfg.label}
    </span>
  );
};

const Banner = ({ type, message, onClose }) => (
  <div style={{ background: type === 'error' ? '#fef2f2' : '#f0fdf4', border: `1px solid ${type === 'error' ? '#fca5a5' : '#86efac'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, color: type === 'error' ? '#b91c1c' : '#15803d', fontSize: 13 }}>
    {type === 'error' ? <AlertCircle size={14} /> : <Check size={14} />}
    <span style={{ flex: 1 }}>{message}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={13} /></button>
  </div>
);

// ─── Create/Edit Modal ─────────────────────────────────────────────────────────
const FormModal = ({ payment, onSave, onClose, isSaving }) => {
  const isEdit = !!payment;
  const [form, setForm] = useState({
    customerName: payment?.customerName || '', invoiceId: payment?.invoiceId || '',
    paymentDate: payment?.paymentDate?.split('T')[0] || '', amount: payment?.amount || 0,
    paymentMethod: payment?.paymentMethod || 'Cash', status: payment?.status || 'Completed',
    reference: payment?.reference || '', notes: payment?.notes || '',
  });
  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.box, maxWidth: 560 }}>
        <div style={ms.head}>
          <h3 style={ms.title}>{isEdit ? 'Edit Payment' : 'Record Payment'}</h3>
          <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
        </div>
        <div style={ms.body}>
          <div style={ms.row2}>
            <div style={ms.fg}><label style={ms.lbl}>Customer Name *</label><input style={ms.inp} value={form.customerName} onChange={(e) => f('customerName', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Invoice ID</label><input style={ms.inp} value={form.invoiceId} onChange={(e) => f('invoiceId', e.target.value)} placeholder="e.g. INV-0001" /></div>
            <div style={ms.fg}><label style={ms.lbl}>Payment Date</label><input style={ms.inp} type="date" value={form.paymentDate} onChange={(e) => f('paymentDate', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Amount (₹) *</label><input style={ms.inp} type="number" min="0" value={form.amount} onChange={(e) => f('amount', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Payment Method</label>
              <select style={ms.inp} value={form.paymentMethod} onChange={(e) => f('paymentMethod', e.target.value)}>
                {Object.keys(METHOD_CONFIG).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={ms.fg}><label style={ms.lbl}>Status</label>
              <select style={ms.inp} value={form.status} onChange={(e) => f('status', e.target.value)}>
                {Object.keys(STATUS_CONFIG).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={ms.fg}><label style={ms.lbl}>Reference / Txn ID</label><input style={ms.inp} value={form.reference} onChange={(e) => f('reference', e.target.value)} /></div>
          <div style={ms.fg}><label style={ms.lbl}>Notes</label><textarea style={{ ...ms.inp, height: 60, resize: 'vertical' }} value={form.notes} onChange={(e) => f('notes', e.target.value)} /></div>
        </div>
        <div style={ms.foot}>
          <button onClick={onClose} style={ms.ghostBtn}>Cancel</button>
          <button onClick={() => onSave(form)} disabled={isSaving || !form.customerName || !form.amount} style={{ ...ms.primaryBtn, opacity: (isSaving || !form.customerName) ? 0.6 : 1 }}>
            {isSaving ? <><Spinner size={13} /> Saving…</> : (isEdit ? 'Update' : 'Record')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────
const PaymentDetail = ({ payment, onClose, onAction, actionLoading, isSuperAdmin }) => {
  if (!payment) return null;
  const canDelete = isSuperAdmin;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 480, background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,.12)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{payment.paymentId}</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              <Badge status={payment.status} />
              <Badge status={payment.paymentMethod} config={METHOD_CONFIG} />
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} /></button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
            <div style={dr.row}><span style={dr.lbl}>Customer</span><span style={dr.val}>{payment.customerName}</span></div>
            <div style={dr.row}><span style={dr.lbl}>Invoice</span><span style={dr.val}>{payment.invoiceId || '—'}</span></div>
            <div style={dr.row}><span style={dr.lbl}>Reference</span><span style={dr.val}>{payment.reference || '—'}</span></div>
            <div style={{ ...dr.row, fontWeight: 700, fontSize: 15, borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
              <span>Amount</span><span>{fmt(payment.amount)}</span>
            </div>
          </div>

          <div style={dr.infoCard}><span style={dr.lbl}>Payment Date</span><span style={dr.val}>{fmtDate(payment.paymentDate)}</span></div>

          {payment.notes && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Notes</div>
              <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>{payment.notes}</p>
            </div>
          )}

          {canDelete && (
            <button disabled={actionLoading.delete} onClick={() => onAction('delete', payment)} style={{ ...dr.actionBtn, background: '#fff', color: '#ef4444', border: '1px solid #fca5a5' }}>
              {actionLoading.delete ? <Spinner size={13} /> : <Trash2 size={13} />} Delete Payment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const PaymentsPage = () => {
  const [payments,      setPayments]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [banner,        setBanner]        = useState(null);
  const [filters,       setFilters]       = useState({ status: '', paymentMethod: '', page: 1, limit: 12, sortBy: 'createdAt', sortOrder: 'desc' });
  const [search,        setSearch]        = useState('');
  const [selected,      setSelected]      = useState(null);
  const [modal,         setModal]         = useState(null); // 'create' | 'edit'
  const [modalPayment,  setModalPayment]  = useState(null);
  const [isSaving,      setIsSaving]      = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const isSuperAdmin = salesPaymentController.isSuperAdmin();

  const notify = useCallback((type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner(null), 4000);
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const activeFilters = { ...filters };
      if (search) activeFilters.search = search;
      const res = await salesPaymentController.getAllPayments(activeFilters);
      setPayments(res.success ? res.data : []);
      if (!res.success) notify('error', res.message);
    } catch (err) {
      notify('error', 'Failed to load payments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, search, notify]);

  useEffect(() => { loadData(false); }, [loadData]);

  const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v, page: 1 }));
  const setActLoading = (k, v) => setActionLoading((p) => ({ ...p, [k]: v }));

  const handleSave = async (formData) => {
    setIsSaving(true);
    const isEdit = !!modalPayment;
    const res = isEdit
      ? await salesPaymentController.updatePayment(modalPayment.id || modalPayment._id, formData)
      : await salesPaymentController.createPayment(formData);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); } else notify('error', res.message);
  };

  const handleDelete = async (payment) => {
    setActLoading('delete', true);
    const res = await salesPaymentController.deletePayment(payment.id || payment._id);
    setActLoading('delete', false);
    if (res.success) { notify('success', res.message); setSelected(null); loadData(true); } else notify('error', res.message);
  };

  const stats = {
    total:     payments.length,
    completed: payments.filter((p) => p.status === 'Completed').length,
    pending:   payments.filter((p) => p.status === 'Pending').length,
    totalAmt:  payments.filter((p) => p.status === 'Completed').reduce((sum, p) => sum + Number(p.amount || 0), 0),
  };

  return (
    <div style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wallet size={22} color="#3b82f6" />Payments
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Offline/manual payments collected against sales invoices</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => loadData(true)} disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#475569' }}>
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />Refresh
          </button>
          <button onClick={() => { setModal('create'); setModalPayment(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#1e293b', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
            <Plus size={14} />Record Payment
          </button>
        </div>
      </div>

      {banner && <Banner type={banner.type} message={banner.message} onClose={() => setBanner(null)} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Payments', value: stats.total,             color: '#1e293b' },
          { label: 'Completed',      value: stats.completed,         color: '#15803d' },
          { label: 'Pending',        value: stats.pending,           color: '#92400e' },
          { label: 'Total Received', value: fmt(stats.totalAmt),     color: '#1d4ed8' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadData(false)}
            placeholder="Search payments..."
            style={{ width: '100%', padding: '8px 8px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
        </div>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none' }}>
          {[['', 'All Status'], ...Object.keys(STATUS_CONFIG).map((s) => [s, s])].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.paymentMethod} onChange={(e) => setFilter('paymentMethod', e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none' }}>
          {[['', 'All Methods'], ...Object.keys(METHOD_CONFIG).map((m) => [m, m])].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, gap: 10, color: '#64748b' }}>
          <Spinner size={28} /><span style={{ fontSize: 14 }}>Loading payments…</span>
        </div>
      ) : payments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <Wallet size={40} color="#e2e8f0" style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14, margin: 0 }}>No payments found</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  {['Payment #', 'Customer', 'Invoice', 'Amount', 'Method', 'Status'].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id || p._id} style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setSelected(p)}>
                    <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{p.paymentId}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 500 }}>{p.customerName}</td>
                    <td style={{ padding: '11px 14px' }}>{p.invoiceId || '—'}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 600 }}>{fmt(p.amount)}</td>
                    <td style={{ padding: '11px 14px' }}><Badge status={p.paymentMethod} config={METHOD_CONFIG} /></td>
                    <td style={{ padding: '11px 14px' }}><Badge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <PaymentDetail payment={selected} onClose={() => setSelected(null)} onAction={(type, p) => type === 'delete' && handleDelete(p)} actionLoading={actionLoading} isSuperAdmin={isSuperAdmin} />
      )}

      {(modal === 'create' || modal === 'edit') && (
        <FormModal payment={modalPayment} onSave={handleSave} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
    </div>
  );
};

const ms = {
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1010, padding: 16 },
  box:        { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 580, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.2)' },
  head:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' },
  title:      { fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 },
  closeBtn:   { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' },
  body:       { padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 },
  foot:       { padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafafa' },
  row2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  fg:         { display: 'flex', flexDirection: 'column', gap: 4 },
  lbl:        { fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' },
  inp:        { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, color: '#1e293b', outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' },
  ghostBtn:   { padding: '8px 16px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#475569' },
  primaryBtn: { padding: '8px 18px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 },
};

const dr = {
  row:       { display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 },
  lbl:       { color: '#64748b' },
  val:       { fontWeight: 500, color: '#1e293b' },
  infoCard:  { background: '#f8fafc', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  section:   { background: '#f8fafc', borderRadius: 8, padding: '12px 14px' },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 },
  actionBtn: { width: '100%', padding: '10px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
};

export default PaymentsPage;